import React from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

const YOUTUBE_URL = 'https://www.youtube.com/' + String.fromCharCode(64) + 'PostgameBUZZ';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-black via-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="relative inline-block group mb-6">
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800/20 to-yellow-600/30 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Logo container with gradient border */}
              <div className="relative bg-gradient-to-br from-gray-800 via-yellow-600 to-black p-0.5 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                <div className="bg-white rounded-[10px] px-6 py-3 flex items-center justify-center">
                  <img 
                    src="/logo.svg" 
                    alt="Postgame Buzz Logo" 
                    className="h-8 w-8 mr-2"
                  />
                  <div className="text-2xl font-black tracking-tight flex items-center">
                    <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-black bg-clip-text text-transparent">
                      POSTGAME
                    </span>
                    <span className="text-yellow-600 ml-0.5">
                      BUZZ
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Subtle shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-500"></div>
            </div>
            <p className="text-gray-400 mb-6 text-sm">
              Bringing youth sports to life with AI-powered GameCasts that make every player feel like a star.
            </p>

            {/* Social Media Links */}
            <div className="flex space-x-4 mt-4">
              <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" title="Visit our YouTube channel">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.745 5 12 5 12 5s6.255 0 7.812.418ZM15.5 12 10 9v6l5.5-3Z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61580150573106" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12Z" clipRule="evenodd" />
                </svg>
              </a>
            </div>

          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-yellow-500">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/create" className="text-gray-400 hover:text-white transition-colors">
                  Create Episode
                </Link>
              </li>
              <li>
                <a href="/#pricing" className="text-gray-400 hover:text-white transition-colors">
                  Pricing
                </a>
              </li>
              <li>
                <HashLink to="#features" className="text-gray-400 hover:text-white transition-colors">
                  Features
                </HashLink>
              </li>
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                  Sample Episodes
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-yellow-500">Resources</h3>
            <ul className="space-y-2">
              <li>
                <a href="/#made-for-teams" className="text-gray-400 hover:text-white transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-400 hover:text-white transition-colors">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <hr className="my-12 border-gray-800" />

        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">
            {new Date().getFullYear()} Postgame Buzz. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-yellow-500 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-500 hover:text-yellow-500 text-sm transition-colors">
              Terms of Service
            </Link>

          </div>
        </div>
        <div className="mt-4 text-center md:text-right">
          <p className="text-gray-500 text-xs">
            Made with for youth sports families
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
